import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';

import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { format, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { RestBasedServiceAuthenticationType } from 'shell-models-lib';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { IDataflowTypeMetadataSource } from 'app/extensibility/metadatas/resource-type/dataflow-format-type-metadata';
import { zendeskEntityOptions } from 'app/shared/utils/connector-metadata-static-consts';

const controls: ControlMetadata[] = [
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'url',
            label: stringResources.ModelName_BaseUrl,
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            warningMessageCalculator: (value: string) =>
                stringIsNullOrEmpty(value) ? '' : stringResources.infoSentViaUrlWarning,
            isLabelVertical: true
        },
        warningMessageCalculator: (value: string) =>
            stringIsNullOrEmpty(value) ? '' : stringResources.infoSentViaUrlWarning
    },
    {
        type: 'select-switch',
        bind: 'authenticationType',
        label: stringResources.ModelName_AuthenticationType,
        required: true,
        options: [
            {
                label: stringResources.Basic_DisplayText,
                value: RestBasedServiceAuthenticationType.BASIC
            },
            {
                label: stringResources.ModelName_Token,
                value: RestBasedServiceAuthenticationType.TOKEN
            }
        ],
        isLabelVertical: true,
        defaultValue: RestBasedServiceAuthenticationType.BASIC,
        cases: [
            {
                caseValue: RestBasedServiceAuthenticationType.BASIC,
                children: [
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'textbox',
                            bind: 'userName',
                            label: stringResources.ModelName_UserName,
                            required: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            isLabelVertical: true
                        }
                    },
                    {
                        type: 'dynamic-akv-switcher',
                        enableAKVParameterization: true,
                        hideAKVSwitcherInConnStrProps: true,
                        bind: 'password',
                        label: stringResources.ModelName_Password,
                        child: {
                            type: 'parameterizable',
                            attribute: 'password',
                            disableAddParameter: true,
                            child:
                                {
                                    type: 'password',
                                    label: stringResources.ModelName_Password,
                                    required: true,
                                    isLabelVertical: true,
                                    validators: [
                                        { type: 'required', errorMessage: '' }
                                    ]
                                }
                        },
                        description: format(
                            stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Zendesk_DisplayText),
                            stringResources.ModelName_Password
                        )
                    }
                ]
            },
            {
                caseValue: RestBasedServiceAuthenticationType.TOKEN,
                children: [
                    {
                        type: 'dynamic-akv-switcher',
                        enableAKVParameterization: true,
                        hideAKVSwitcherInConnStrProps: true,
                        bind: 'apiToken',
                        label: stringResources.ModelName_Token,
                        child: {
                            type: 'parameterizable',
                            attribute: 'apiToken',
                            disableAddParameter: true,
                            child: {
                                type: 'password',
                                label: stringResources.ModelName_Token,
                                required: true,
                                isLabelVertical: true,
                                validators: [
                                    { type: 'required', errorMessage: '' }
                                ]
                            }
                        },
                        description: format(
                            stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Zendesk_DisplayText),
                            stringResources.ModelName_Token
                        )
                    }
                ]
            }
        ]
    }
];

const dataflowZendeskSource: IDataflowTypeMetadataSource = {
    type: DatasetFormatType.Zendesk,
    displayName: stringResources.Zendesk_DisplayText,
    settings: adfModelTypeMap.zendesk.dataflowSettings,
    connectTransformationProperties: ['entity'],
    controls: [
        {
            type: 'parameterizable',
            child: {
                type: 'select',
                bind: 'entity',
                label: stringResources.DataFlow_Entity,
                isDataFlowExpression: true,
                options: Object.values(zendeskEntityOptions).map((entity) => ({ label: entity, value: entity })).sort((a, b) => a.label.localeCompare(b.label)),
                required: true,
                editable: true,
                validators: [
                    { type: 'required', errorMessage: '' }
                ],
                defaultValue: zendeskEntityOptions.TICKETS
            },
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true
        }
    ],
    useInlineProjection: true
};

export const zendeskMetadata: ILinkedServiceTypeMetadata = {
    type: adfModelTypeMap.zendesk.linkedServiceType,
    displayName: format(stringResources.ModelName_DisplayTextPreviewTemplate, stringResources.Zendesk_DisplayText),
    linkedServiceKind: 'unknown',
    icon: {
        className: 'svg-i-100x100-zendesk'
    },
    category: ['ServicesApps'],
    onlyAzureIR: true,
    copySourceTypes: [],
    copySinkTypes: [],
    isFirstClass: true,
    supportDataFlowSource: true,
    dataflowSource: [dataflowZendeskSource],
    supportFirstClassParameterExperience: true,
    controls
};
